import React from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { Rating } from 'primereact/rating'
import { Chip } from 'primereact/chip'
import { fetchAllShops, Shop } from '../api/shops'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate } from 'react-router-dom'
import BreadcrumbsNav from '../components/BreadcrumbsNav'

const ShopDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [shop, setShop] = React.useState<Shop | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    const loadShop = async () => {
      if (!id) return
      try {
        setLoading(true)
        const shops = await fetchAllShops()
        const foundShop = shops.find(s => s.id.toString() === id)
        setShop(foundShop || null)
      } catch (error) {
        console.error('Failed to load shop:', error)
        setShop(null)
      } finally {
        setLoading(false)
      }
    }
    loadShop()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex align-items-center justify-content-center bg-gray-50">
        <div className="text-center">
          <i className="pi pi-spin pi-spinner text-orange-500 text-4xl mb-3"></i>
          <div className="text-orange-500 text-xl font-semibold">Loading...</div>
        </div>
      </div>
    )
  }

  if (!shop) {
    return (
      <div className="min-h-screen flex flex-column align-items-center justify-content-center bg-gray-50">
        <h2 className="text-4xl font-bold mb-4">Shop not found</h2>
        <Button
          label="Back to Shops"
          icon="pi pi-arrow-left"
          onClick={() => navigate('/shops')}
          className="bg-orange-600 border-orange-600"
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-column bg-gray-50">
      <Helmet>
        <title>{i18n.language === 'ta' ? shop.nameTamil : i18n.language === 'de' ? shop.nameGerman : shop.name} · YarlHub</title>
        <meta name="description" content={i18n.language === 'ta' ? shop.descriptionTamil : i18n.language === 'de' ? shop.descriptionGerman : shop.description} />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : undefined} />
      </Helmet>

      <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
        {/* Header Section */}
        <div className="mb-5">
          <Button
            label={t('back') || 'Back'}
            icon="pi pi-arrow-left"
            className="p-button-text text-orange-600 mb-2 pl-0 hover:bg-orange-50"
            onClick={() => navigate('/shops')}
          />

          <BreadcrumbsNav items={[
            { label: t('site.shops') },
            { label: i18n.language === 'ta' ? (shop.nameTamil || shop.name) : i18n.language === 'de' ? (shop.nameGerman || shop.name) : shop.name }
          ]} />
        </div>

        {/* Shop Header */}
        <Card className="mb-5 border-none shadow-2 overflow-hidden border-round-xl">
          <img
            src={shop.imageUrl}
            alt={shop.name}
            className="w-full h-20rem object-cover"
            style={{ maxHeight: '400px' }}
          />
          <div className="p-4 lg:p-6">
            <div className="flex flex-column lg:flex-row justify-content-between align-items-start gap-4 mb-4">
              <div className="flex-1">
                <div className="flex align-items-center gap-3 mb-3">
                  <h1 className="text-4xl md:text-5xl font-bold font-serif text-900 m-0">
                    {i18n.language === 'ta' ? shop.nameTamil : i18n.language === 'de' ? shop.nameGerman : shop.name}
                  </h1>
                  {shop.isVerified && (
                    <i className="pi pi-check-circle text-orange-500 text-3xl" title="Verified Shop"></i>
                  )}
                </div>

                <Chip
                  label={i18n.language === 'ta' ? shop.categoryTamil : i18n.language === 'de' ? shop.categoryGerman : shop.category}
                  className="bg-orange-500 text-white font-bold mb-3"
                />

                <div className="flex align-items-center gap-3 mb-3">
                  <Rating value={shop.rating} readOnly stars={5} cancel={false} />
                  <span className="text-gray-600 font-semibold text-lg">
                    {shop.rating} ({shop.reviewCount} reviews)
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button icon="pi pi-share-alt" className="p-button-rounded p-button-outlined border-orange-500 text-orange-500" tooltip="Share" />
                <Button icon="pi pi-heart" className="p-button-rounded p-button-outlined border-orange-500 text-orange-500" tooltip="Favorite" />
              </div>
            </div>

            <p className="text-gray-700 text-lg line-height-3 mb-4">
              {i18n.language === 'ta' ? shop.descriptionTamil : i18n.language === 'de' ? shop.descriptionGerman : shop.description}
            </p>
          </div>
        </Card>

        <div className="grid">
          {/* Main Content */}
          <div className="col-12 md:col-8">
            {/* Contact Information */}
            <Card className="mb-4 border-none shadow-2 border-round-xl" title="Contact Information">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <div className="flex flex-column gap-4">
                    <div className="flex gap-3">
                      <i className="pi pi-map-marker text-orange-500 text-2xl"></i>
                      <div>
                        <div className="text-gray-500 text-sm mb-1">Address</div>
                        <div className="font-semibold text-900">
                          {i18n.language === 'ta' ? shop.addressTamil : i18n.language === 'de' ? shop.addressGerman : shop.address}
                        </div>
                        <div className="font-semibold text-900">
                          {i18n.language === 'ta' ? shop.cityTamil : i18n.language === 'de' ? shop.cityGerman : shop.city}, {shop.country}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <i className="pi pi-phone text-orange-500 text-2xl"></i>
                      <div>
                        <div className="text-gray-500 text-sm mb-1">Phone</div>
                        <div className="font-semibold text-900">{shop.phone}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="flex flex-column gap-4">
                    <div className="flex gap-3">
                      <i className="pi pi-envelope text-orange-500 text-2xl"></i>
                      <div>
                        <div className="text-gray-500 text-sm mb-1">Email</div>
                        <div className="font-semibold text-900">{shop.email}</div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <i className="pi pi-globe text-orange-500 text-2xl"></i>
                      <div>
                        <div className="text-gray-500 text-sm mb-1">Website</div>
                        <a
                          href={`https://${shop.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-orange-500 no-underline hover:underline"
                        >
                          {shop.website}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Specialties */}
            <Card className="mb-4 border-none shadow-2 border-round-xl" title="Specialties">
              <div className="flex gap-2 flex-wrap">
                {shop.specialties?.map((specialty, index) => (
                  <Chip
                    key={index}
                    label={i18n.language === 'ta' ? shop.specialtiesTamil?.[index] : i18n.language === 'de' ? shop.specialtiesGerman?.[index] : specialty}
                    className="bg-orange-50 text-orange-600 border-1 border-orange-200 font-semibold"
                  />
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="col-12 md:col-4">
            {/* Opening Hours */}
            <Card className="mb-4 border-none shadow-2 border-round-xl" title="Opening Hours">
              <div className="flex gap-3">
                <i className="pi pi-clock text-orange-500 text-xl mt-1"></i>
                <div className="text-gray-700 line-height-3 white-space-pre-line">
                  {i18n.language === 'ta' ? shop.openingHoursTamil : i18n.language === 'de' ? shop.openingHoursGerman : shop.openingHours}
                </div>
              </div>
            </Card>

            {/* Shop Info */}
            <Card className="mb-4 border-none shadow-2 border-round-xl" title="Shop Information">
              <div className="flex flex-column gap-3">
                <div>
                  <div className="text-gray-500 text-sm mb-1">Owner</div>
                  <div className="font-semibold text-900">
                    {i18n.language === 'ta' ? shop.ownerTamil : i18n.language === 'de' ? shop.ownerGerman : shop.owner}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm mb-1">Established</div>
                  <div className="font-semibold text-900">{shop.establishedYear}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm mb-1">Languages</div>
                  <div className="font-semibold text-900">{shop.languages?.join(', ')}</div>
                </div>
              </div>
            </Card>

            {/* Action Buttons */}
            <Card className="border-none shadow-2 border-round-xl">
              <div className="flex flex-column gap-3">
                <Button
                  label="Call Now"
                  icon="pi pi-phone"
                  className="w-full bg-orange-600 border-orange-600 font-bold"
                  onClick={() => window.location.href = `tel:${shop.phone}`}
                />
                <Button
                  label="Send Email"
                  icon="pi pi-envelope"
                  className="w-full p-button-outlined border-orange-600 text-orange-600 font-bold"
                  onClick={() => window.location.href = `mailto:${shop.email}`}
                />
                <Button
                  label="Visit Website"
                  icon="pi pi-globe"
                  className="w-full p-button-outlined border-orange-600 text-orange-600 font-bold"
                  onClick={() => window.open(`https://${shop.website}`, '_blank')}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShopDetailPage
